/**
 * Regulatory Node Data — Static reference for deterministic matching (DRR-075).
 *
 * Extracted from regulatory-nodes configs. Used by the RegulatoryProfileStep
 * to suggest applicable regulatory bodies based on NAICS industry + state.
 *
 * The LLM enhancement layer can override confidence/reasons, but the
 * deterministic match provides instant baseline suggestions.
 */

// Jurisdiction type from useRegulatoryConstellation (not re-exported from scope barrel
// to avoid conflict with Jurisdiction interface in @ferp/workflow-forms types.ts)
type Jurisdiction = 'federal' | 'state' | 'payer' | 'standard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RegulatoryNodeData {
  /** Matches regulatory-nodes config node_id. */
  readonly nodeId: string;
  /** Human-readable name. */
  readonly name: string;
  /** Jurisdiction level — determines ring placement. */
  readonly jurisdiction: Jurisdiction;
  /** Type of authority. */
  readonly authorityType: string;
  /** Enforcement body name. */
  readonly enforcementBody: string;
  /** State code for state-level nodes (e.g., "MN"). */
  readonly state?: string;
  /** Industries this node applies to — key for deterministic matching. */
  readonly appliesToIndustries: readonly string[];
  /** Default edge type: REGULATED_BY (mandatory) or GOVERNED_BY (voluntary). */
  readonly defaultEdgeType: 'REGULATED_BY' | 'GOVERNED_BY';
  /** Default reason if LLM doesn't provide one. */
  readonly defaultReason: string;
}

// ---------------------------------------------------------------------------
// NAICS Sector → Industry Keyword Mapping
// ---------------------------------------------------------------------------

/**
 * Maps NAICS sector codes to industry keywords used in regulatory-nodes configs
 * `applies_to_industries` fields.
 *
 * A single NAICS sector may map to multiple industry keywords.
 */
export const NAICS_TO_INDUSTRIES: Record<string, readonly string[]> = {
  '62': ['assisted_living', 'home_care', 'dental', 'hospital', 'pharmacy', 'mental_health', 'health_insurance'],
  '72': ['food_service'],
  '23': ['construction'],
  '54': ['accounting'],
  '52': ['finance', 'insurance'],
};

/**
 * Maps NAICS subsector codes (3-digit) to more specific industry keywords.
 * Takes priority over sector-level mapping when available.
 */
export const NAICS_SUBSECTOR_TO_INDUSTRIES: Record<string, readonly string[]> = {
  '621': ['home_care', 'dental', 'pharmacy', 'mental_health'],      // Ambulatory Health Care
  '622': ['hospital'],                                                // Hospitals
  '623': ['assisted_living', 'adult_foster_care', 'day_services'],   // Nursing / Residential Care
  '624': ['day_services'],                                            // Social Assistance
};

// ---------------------------------------------------------------------------
// Universal Regulatory Nodes (apply to all employers)
// ---------------------------------------------------------------------------

/**
 * Node IDs that apply to ANY employer regardless of industry.
 * These get a lower default confidence (0.7) since they're broad.
 */
export const UNIVERSAL_EMPLOYER_NODES = new Set(['osha']);

/**
 * Node IDs that apply to any business handling health information.
 * These get high confidence (0.95) for healthcare NAICS sectors.
 */
export const HEALTHCARE_UNIVERSAL_NODES = new Set(['hipaa']);

// ---------------------------------------------------------------------------
// All Regulatory Nodes (from regulatory-nodes configs)
// ---------------------------------------------------------------------------

export const REGULATORY_NODES: readonly RegulatoryNodeData[] = [
  // ── Federal ──────────────────────────────────────────────────────────────
  {
    nodeId: 'hipaa',
    name: 'HIPAA',
    jurisdiction: 'federal',
    authorityType: 'privacy_security',
    enforcementBody: 'HHS Office for Civil Rights (OCR)',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental', 'hospital', 'health_insurance', 'pharmacy', 'mental_health'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Protected Health Information (PHI) protection and data privacy compliance required',
  },
  {
    nodeId: 'osha',
    name: 'OSHA',
    jurisdiction: 'federal',
    authorityType: 'workplace_safety',
    enforcementBody: 'US Department of Labor — OSHA',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental', 'hospital', 'food_service', 'construction', 'accounting'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Workplace safety, PPE compliance, and hazard communication standards required',
  },
  {
    nodeId: 'cms',
    name: 'CMS',
    jurisdiction: 'federal',
    authorityType: 'regulator_payer',
    enforcementBody: 'HHS — Centers for Medicare & Medicaid Services',
    appliesToIndustries: ['assisted_living', 'home_care', 'hospital', 'dental', 'pharmacy'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Medicare/Medicaid Conditions of Participation and billing requirements',
  },
  {
    nodeId: 'cms-pdgm',
    name: 'CMS-PDGM',
    jurisdiction: 'federal',
    authorityType: 'payment_model',
    enforcementBody: 'CMS — Center for Medicare',
    appliesToIndustries: ['home_care'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Home health 30-day payment period, OASIS assessment, and clinical grouping compliance',
  },
  {
    nodeId: 'oig',
    name: 'OIG',
    jurisdiction: 'federal',
    authorityType: 'fraud_enforcement',
    enforcementBody: 'HHS — Office of Inspector General',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental', 'hospital', 'pharmacy'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Anti-fraud, anti-kickback statute compliance, and exclusion list screening required',
  },

  // ── State: Minnesota ────────────────────────────────────────────────────
  {
    nodeId: 'mn-245d',
    name: 'MN 245D',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'licensing',
    enforcementBody: 'Minnesota Department of Human Services (DHS)',
    appliesToIndustries: ['assisted_living', 'adult_foster_care', 'day_services'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Assisted living licensure, medication management, and incident reporting compliance',
  },
  {
    nodeId: 'mn-home-care-license',
    name: 'MN Home Care License',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'licensing',
    enforcementBody: 'Minnesota Department of Health (MDH)',
    appliesToIndustries: ['home_care'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Home care agency licensing, RN supervision, and competency evaluation requirements',
  },
  {
    nodeId: 'mn-dhs-elderly-waiver',
    name: 'MN DHS Elderly Waiver',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'funding_program',
    enforcementBody: 'Minnesota Department of Human Services',
    appliesToIndustries: ['assisted_living', 'home_care'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Medicaid waiver program eligibility, service authorization, and rate compliance',
  },
  {
    nodeId: 'mn-board-of-pharmacy',
    name: 'MN Board of Pharmacy',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'professional_licensing',
    enforcementBody: 'Minnesota Board of Pharmacy',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental', 'hospital'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Controlled substance handling and medication storage requirements',
  },
  {
    nodeId: 'mn-board-of-dentistry',
    name: 'MN Board of Dentistry',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'professional_licensing',
    enforcementBody: 'Minnesota Board of Dentistry',
    appliesToIndustries: ['dental'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Dental practice licensure, scope of practice, and infection control standards',
  },
  {
    nodeId: 'mn-radiation-control',
    name: 'MN Radiation Control',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'safety',
    enforcementBody: 'Minnesota Department of Health — Radiation Control',
    appliesToIndustries: ['dental', 'hospital'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Radiographic equipment registration and operator certification requirements',
  },
  {
    nodeId: 'mn-dept-of-revenue',
    name: 'MN Dept of Revenue',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'taxation',
    enforcementBody: 'Minnesota Department of Revenue',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental', 'food_service', 'construction', 'accounting'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Payroll withholding, quarterly tax filing, and MinnesotaCare provider tax compliance',
  },

  // ── Payers ──────────────────────────────────────────────────────────────
  {
    nodeId: 'ucare-mn',
    name: 'UCare Minnesota',
    jurisdiction: 'payer',
    state: 'MN',
    authorityType: 'payer',
    enforcementBody: 'UCare Minnesota',
    appliesToIndustries: ['assisted_living', 'home_care', 'dental'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Payer credentialing, prior authorization, and claims format compliance',
  },
  {
    nodeId: 'delta-dental-mn',
    name: 'Delta Dental MN',
    jurisdiction: 'payer',
    state: 'MN',
    authorityType: 'payer',
    enforcementBody: 'Delta Dental of Minnesota',
    appliesToIndustries: ['dental'],
    defaultEdgeType: 'REGULATED_BY',
    defaultReason: 'Dental payer credentialing, frequency limits, and claims format compliance',
  },

  // ── Standards (voluntary) ──────────────────────────────────────────────
  {
    nodeId: 'ada-standards',
    name: 'ADA Standards',
    jurisdiction: 'standard',
    authorityType: 'professional_standards',
    enforcementBody: 'American Dental Association (voluntary)',
    appliesToIndustries: ['dental'],
    defaultEdgeType: 'GOVERNED_BY',
    defaultReason: 'Industry standard CDT coding, infection control guidelines, and record-keeping practices',
  },
] as const;

// ---------------------------------------------------------------------------
// Lookup Helpers
// ---------------------------------------------------------------------------

const NODE_MAP = new Map(REGULATORY_NODES.map((n) => [n.nodeId, n]));

/** Get a regulatory node by ID. */
export function getRegulatoryNode(nodeId: string): RegulatoryNodeData | undefined {
  return NODE_MAP.get(nodeId);
}

/** Get all nodes matching a set of industry keywords. */
export function getNodesForIndustries(industries: readonly string[]): RegulatoryNodeData[] {
  const industrySet = new Set(industries);
  return REGULATORY_NODES.filter((node) =>
    node.appliesToIndustries.some((ind) => industrySet.has(ind)),
  );
}

/** Get all nodes matching a state code. */
export function getNodesForState(stateCode: string): RegulatoryNodeData[] {
  return REGULATORY_NODES.filter(
    (node) => !node.state || node.state === stateCode,
  );
}

/**
 * Get industry keywords for a NAICS sector + optional subsector.
 * Subsector takes priority when available.
 */
export function getIndustriesForNaics(
  sectorCode: string,
  subsectorCode?: string,
): readonly string[] {
  if (subsectorCode && NAICS_SUBSECTOR_TO_INDUSTRIES[subsectorCode]) {
    return NAICS_SUBSECTOR_TO_INDUSTRIES[subsectorCode];
  }
  return NAICS_TO_INDUSTRIES[sectorCode] ?? [];
}
